import type { RoadmapStage } from 'src/constants/client-roadmap';
import type { FragmentType } from 'src/gql';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Circle } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';
import { useIsVerySmallScreen } from 'src/hooks/use-is-very-small-screen';
import {
  isRequiredTaskStatus,
  isSignedRequiredOnboardingTask,
  roadmapStageBody,
  roadmapStageTitle,
  roadmapStageToTaskType,
} from 'src/utils/client-roadmap';

import type { TaskStatus, TaskTypeName } from '@savvy/constants/generated/gql-enums';

import GuidedFlowStepDoneIcon from '../../icons/custom/done';
import RoadmapCallToAction from './roadmap-call-to-action';

export const RoadmapStageCardFragment = graphql(/* GraphQL */ `
  fragment RoadmapStageCard on Task {
    type
    status
    maybeDocusignSigned
    onboardingTask {
      coClientDocusignSignedAt
      coClientIaaSignedAt
      primaryClientDocusignSignedAt
      primaryClientIaaSignedAt
    }
    user {
      isPrimaryClient
    }
    ...RoadmapCallToAction
  }
`);

/**
 * @deprecated MUI is deprecated. Use the @savvy/savvy-kit library instead. Use the @savvy/savvy-kit library instead.
 */
function RoadmapStageCard({
  isCurrentStage,
  disabled,
  roadmapStage,
  stageNumber,
  numStages,
  taskFrags,
  width,
}: {
  readonly isCurrentStage: boolean;
  readonly disabled: boolean;
  readonly roadmapStage: RoadmapStage;
  readonly numStages: number;
  readonly stageNumber: number;
  readonly taskFrags: FragmentType<typeof RoadmapStageCardFragment>[];
  readonly width?: number;
}) {
  const isVerySmallScreen = useIsVerySmallScreen();
  const tasks = taskFrags.map((frag) => unmaskFragment(RoadmapStageCardFragment, frag));
  const isClientDoneWithTask = areAllRequiredTasksDoneForStage({
    tasks,
    roadmapStage,
  });
  const hasRequiredTasksForStage =
    tasks.filter((task) => task.type === roadmapStageToTaskType[roadmapStage]).length > 0;
  return (
    <div
      className={cn(
        'flex flex-col bg-card rounded-lg border border-border-light',
        disabled ? 'opacity-20' : 'opacity-100',
      )}
      style={{ width }}
    >
      <div className="flex flex-row justify-between px-6 py-5">
        <div>
          {}
          <Text variant="overline" color="dark">{`${stageNumber + 1} of ${numStages}`}</Text>
        </div>
        <div>{isClientDoneWithTask ? <GuidedFlowStepDoneIcon /> : <Circle color="#E8EAED" />}</div>
      </div>
      <Separator />
      <div className="p-6">
        <div className="my-2">
          <Text variant={isVerySmallScreen ? 'subtitle-sm' : 'subtitle-lg'}>{roadmapStageTitle(roadmapStage)}</Text>
        </div>
        <div className="mb-6">
          <Text variant={isVerySmallScreen ? 'body-sm' : 'body'}>{roadmapStageBody(roadmapStage)}</Text>
        </div>
        {isCurrentStage && hasRequiredTasksForStage && !isClientDoneWithTask && (
          <RoadmapCallToAction roadmapStage={roadmapStage} taskFrags={tasks} />
        )}
      </div>
    </div>
  );
}

export default RoadmapStageCard;

type AreAllRequiredTasksDoneForStageParams = {
  tasks: {
    type: TaskTypeName | null | undefined;
    status: TaskStatus | null | undefined;
    maybeDocusignSigned: boolean | null | undefined;
    onboardingTask: {
      coClientIaaSignedAt: string | null | undefined;
      coClientDocusignSignedAt: string | null | undefined;
      primaryClientIaaSignedAt: string | null | undefined;
      primaryClientDocusignSignedAt: string | null | undefined;
    } | null;
    user: {
      isPrimaryClient: boolean;
    };
  }[];
  roadmapStage: RoadmapStage;
};
function areAllRequiredTasksDoneForStage({ tasks, roadmapStage }: AreAllRequiredTasksDoneForStageParams) {
  const taskTypeForRoadmapStage = roadmapStageToTaskType[roadmapStage];
  const hasRequiredTasksForStage = tasks.some((task) => {
    return task.type === taskTypeForRoadmapStage && isRequiredTaskStatus({ status: task.status });
  });
  if (!hasRequiredTasksForStage) return false;

  return !tasks.some((task) => {
    if (taskTypeForRoadmapStage === 'onboarding_flow') {
      return !isSignedRequiredOnboardingTask({
        docusignSignedAt: task.user.isPrimaryClient
          ? task.onboardingTask?.primaryClientDocusignSignedAt
          : task.onboardingTask?.coClientDocusignSignedAt,
        iaaSignedAt: task.user.isPrimaryClient
          ? task.onboardingTask?.primaryClientIaaSignedAt
          : task.onboardingTask?.coClientIaaSignedAt,
        status: task.status,
      });
    }

    return task.status === 'todo' || task.status === 'in_progress';
  });
}
